/**
 * /whereami client — IP→country lookup for the branded iframe.
 *
 * Why this exists
 * ---------------
 * `allstar-partner-api`'s playlist endpoint is response-cached upstream
 * keyed on path + query string. To get a geo-targeted ad campaign without
 * cross-contaminating viewers, the playlist URL must include an explicit
 * `&geo=XX` so the cache partitions by country. This helper asks the
 * uncached `ex-lambdas` endpoint to resolve the caller's IP to a country
 * code before we build the playlist URL.
 *
 * Failure semantics
 * -----------------
 * Returns `null` for every failure mode — network error, non-2xx,
 * malformed body, missing env var, aborted request. The caller treats
 * `null` as "no geo override" and omits `&geo=` from the playlist URL,
 * which after partner-api PR #1054 means partner-api will omit
 * `adCampaign` entirely. That degrades cleanly (unbranded playlist)
 * instead of risking the cache-contamination bug we are trying to escape.
 */

/**
 * Fetch the caller's country code from the ex-lambdas /whereami endpoint.
 *
 * @param signal AbortSignal — pass the playlist fetch's controller so
 *               unmount cancels both legs together.
 * @returns ISO 3166-1 alpha-2 country code, or `null` on any failure.
 */
export const resolveGeoFromWhereami = async (
  signal?: AbortSignal,
): Promise<string | null> => {
  const url = process.env.REACT_APP_WHEREAMI_URL;
  if (!url) return null;
  try {
    const res = await fetch(url, {
      method: "GET",
      // Don't send cookies — this endpoint doesn't read them and CORS
      // for cookie-bearing requests would require credentials handling
      // on the lambda side.
      credentials: "omit",
      signal,
    });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    if (!data || typeof data !== "object") return null;
    const country = (data as { country?: unknown }).country;
    if (typeof country !== "string" || country.length === 0) return null;
    return country.toUpperCase();
  } catch {
    // Network error, abort, or JSON parse failure. All collapse to "no
    // geo" — the playlist fetch will proceed without &geo=.
    return null;
  }
};
